'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import type { FC, ReactElement } from 'react';
import { Timer } from '@/components/atoms/Timer';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import { Button } from '@/shared/components/Button';
import { getCurrentCompetitonOptions } from './api/getCurrentCompetiton';
import { LeaderBoardHeader } from './components/LeaderBoardHeader';
import { LeaderList } from './components/LeaderList';
import { LeaderListSkeleton } from './components/LeaderListSkeleton';
import { PointsToTokensCard } from './components/PointsToTokensCard';
import { PointsToTokensCardSkeleton } from './components/PointsToTokensCard/PointsToTokensCardSkeleton';
import { TimerSkeleton } from './components/TimerSkeleton';
import { useLeaderboardLiveData, useLeaderboardSocket } from './hooks';
type LeaderBoardProps = {
  sectionClassName?: string;
};
export const LeaderBoard: FC<LeaderBoardProps> = t0 => {
  const $ = _c(45);
  const {
    sectionClassName
  } = t0;
  const t = useTranslations("LeaderBoard.Main");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      enabled: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useLeaderboardSocket(t1);
  const user = useUserStore(_temp);
  const locale = useLocale();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getCurrentCompetitonOptions();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: currentCompetition,
    isLoading: isCompetitionLoading
  } = useQuery(t2);
  const {
    liveData,
    isLoading
  } = useLeaderboardLiveData();
  let t3;
  if ($[2] !== sectionClassName) {
    t3 = clsx("flex w-full flex-col items-center justify-start bg-[#131424] px-4 py-4 pb-16  2md:py-16 ", sectionClassName);
    $[2] = sectionClassName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LeaderBoardHeader />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isLoading || $[6] !== liveData || $[7] !== t) {
    t5 = isLoading ? <><TimerSkeleton withoutMd={true} /><div className="h-[20px] w-[200px] animate-pulse rounded bg-white/20 md:h-[24px] md:w-[250px]" /></> : liveData && <><Timer withoutMd={true} time={new Date(liveData.endDateTime)} /><span className="flex gap-2 text-center align-middle text-[16px] font-normal leading-[1.15] text-[#FFFFFF] md:text-[20px]">{t("Timer.1")} {t("Timer.2")}</span></>;
    $[5] = isLoading;
    $[6] = liveData;
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <div className="mt-8 flex flex-col items-center justify-center gap-2 sm-tablet:gap-2">{t5}</div>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== currentCompetition?.userCompetition?.poeEarned || $[12] !== currentCompetition?.userCompetition?.tokensEarned || $[13] !== isCompetitionLoading || $[14] !== user) {
    t7 = user && <div className="mt-8 w-full max-w-[664px]">{isCompetitionLoading ? <PointsToTokensCardSkeleton /> : <PointsToTokensCard title="points you earned" subtitle="tokens you can get" points={currentCompetition?.userCompetition?.poeEarned || 0} tokens={currentCompetition?.userCompetition?.tokensEarned || 0} />}</div>;
    $[11] = currentCompetition?.userCompetition?.poeEarned;
    $[12] = currentCompetition?.userCompetition?.tokensEarned;
    $[13] = isCompetitionLoading;
    $[14] = user;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("Caption");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="mt-11 w-full whitespace-pre text-wrap text-center text-[14px] leading-[1.5] text-[rgba(255,255,255,0.7)] 2md:text-[16px]">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isLoading || $[21] !== liveData?.entries || $[22] !== user) {
    t10 = <div className="my-10 flex w-full items-center justify-center">{isLoading ? <LeaderListSkeleton groupsCount={3} itemsPerGroup={2} /> : <LeaderList user={user} dataList={liveData?.entries} />}</div>;
    $[20] = isLoading;
    $[21] = liveData?.entries;
    $[22] = user;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t || $[25] !== user) {
    t11 = !user && <span className="mb-4 text-center text-[16px] text-white opacity-70">{t("SigninToUnlock")}</span>;
    $[24] = t;
    $[25] = user;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = user ? `/${locale}/earn` : `/${locale}/auth`;
  let t13;
  if ($[27] !== t || $[28] !== user) {
    t13 = user ? t("Button.DO_MORE") : t("Button.JOIN");
    $[27] = t;
    $[28] = user;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <Button variant="gradient" size="2md" className="w-full min-w-[288px] md:min-w-[300px]">{t13}</Button>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <Link href={t12}>{t14}</Link>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t15 || $[38] !== t6 || $[39] !== t7 || $[40] !== t9) {
    t16 = <div className="flex max-w-[760px] flex-col items-center justify-start ">{t4}{t6}{t7}{t9}{t10}{t11}{t15}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t15;
    $[38] = t6;
    $[39] = t7;
    $[40] = t9;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t16 || $[43] !== t3) {
    t17 = <section className={t3}>{t16}</section>;
    $[42] = t16;
    $[43] = t3;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
};
function _temp(state) {
  return state.user;
}